import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import {
    EditSpreadMoverPayload,
    getSpreadMover,
    saveSpreadMover,
    SpreadMoverDirection,
    SpreadMoverSettingsData,
} from "../../api/spreadMover";
import { useAlertService } from "../../hooks/useAlertService";

interface Props {
    connectionId: string;
    pairLabel: string;
}

type EditableDecimalField =
    | "shift_min"
    | "shift_max"
    | "min_spread_move"
    | "max_spread_move"
    | "min_spread"
    | "max_spread"
    | "order_amount_min"
    | "order_amount_max"
    | "min_step_price"
    | "max_step_price";

interface SpreadMoverForm {
    is_active: boolean;
    check_time_min: string;
    check_time_max: string;
    shift_min: string;
    shift_max: string;
    min_spread_move: string;
    max_spread_move: string;
    min_spread: string;
    max_spread: string;
    order_amount_min: string;
    order_amount_max: string;
    min_step_price: string;
    max_step_price: string;
    direction_mode: SpreadMoverDirection;
}

interface FieldDefinition {
    key: EditableDecimalField;
    label: string;
    hint: string;
    suffix?: string;
}

interface FieldGroup {
    title: string;
    description: string;
    fields: FieldDefinition[];
}

const FIELD_GROUPS: FieldGroup[] = [
    {
        title: "Размер сдвига",
        description: "На сколько процентов от текущей цены сдвигаются лучшие bid и ask.",
        fields: [
            { key: "shift_min", label: "Минимальный сдвиг", hint: "Нижняя граница случайного сдвига", suffix: "%" },
            { key: "shift_max", label: "Максимальный сдвиг", hint: "Верхняя граница случайного сдвига", suffix: "%" },
        ],
    },
    {
        title: "Ценовой коридор",
        description: "Автомат не позволит лучшим ценам выйти за эти границы.",
        fields: [
            { key: "min_spread_move", label: "Нижняя цена", hint: "Минимально допустимый best bid" },
            { key: "max_spread_move", label: "Верхняя цена", hint: "Максимально допустимый best ask" },
        ],
    },
    {
        title: "Ширина спреда",
        description: "Допустимая разница между новой ценой продажи и покупки.",
        fields: [
            { key: "min_spread", label: "Минимальный спред", hint: "Слишком узкий спред не будет применён" },
            { key: "max_spread", label: "Максимальный спред", hint: "Слишком широкий спред не будет применён" },
        ],
    },
    {
        title: "Новые ордера",
        description: "Диапазон случайного объёма для ордеров, создаваемых после сдвига.",
        fields: [
            { key: "order_amount_min", label: "Минимальный объём", hint: "Нижняя граница объёма ордера" },
            { key: "order_amount_max", label: "Максимальный объём", hint: "Верхняя граница объёма ордера" },
        ],
    },
    {
        title: "Заполнение сетки",
        description: "Шаг между новыми ценами при заполнении разрыва до оставшихся ордеров.",
        fields: [
            { key: "min_step_price", label: "Минимальный шаг цены", hint: "Самый плотный шаг сетки" },
            { key: "max_step_price", label: "Максимальный шаг цены", hint: "Самый широкий шаг сетки" },
        ],
    },
];

const RANGE_PAIRS: Array<[keyof SpreadMoverForm, keyof SpreadMoverForm, string]> = [
    ["check_time_min", "check_time_max", "Максимальный интервал не может быть меньше минимального"],
    ["shift_min", "shift_max", "Максимальный сдвиг не может быть меньше минимального"],
    ["min_spread_move", "max_spread_move", "Верхняя цена не может быть меньше нижней"],
    ["min_spread", "max_spread", "Максимальный спред не может быть меньше минимального"],
    ["order_amount_min", "order_amount_max", "Максимальный объём не может быть меньше минимального"],
    ["min_step_price", "max_step_price", "Максимальный шаг не может быть меньше минимального"],
];

const toForm = (data: SpreadMoverSettingsData): SpreadMoverForm => ({
    is_active: data.is_active,
    check_time_min: String(data.check_time_min),
    check_time_max: String(data.check_time_max),
    shift_min: String(data.shift_min),
    shift_max: String(data.shift_max),
    min_spread_move: String(data.min_spread_move),
    max_spread_move: String(data.max_spread_move),
    min_spread: String(data.min_spread),
    max_spread: String(data.max_spread),
    order_amount_min: String(data.order_amount_min),
    order_amount_max: String(data.order_amount_max),
    min_step_price: String(data.min_step_price),
    max_step_price: String(data.max_step_price),
    direction_mode: data.direction_mode,
});

const getErrorMessage = (error: unknown) => {
    if (axios.isAxiosError(error)) {
        const detail = error.response?.data?.detail;
        const message = error.response?.data?.message;
        if (typeof detail === "string") return detail;
        if (typeof message === "string") return message;
    }
    return "Не удалось сохранить настройки";
};

const formatDate = (value: string | null | undefined) => {
    if (!value) return "—";
    return new Date(value).toLocaleString("ru-RU");
};

export function SpreadMoverSettings({ connectionId, pairLabel }: Props) {
    const [form, setForm] = useState<SpreadMoverForm | null>(null);
    const [serverData, setServerData] = useState<SpreadMoverSettingsData | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const { showAlert } = useAlertService();

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError(false);
        try {
            const data = await getSpreadMover(connectionId);
            setServerData(data);
            setForm(toForm(data));
        } catch {
            setLoadError(true);
        } finally {
            setLoading(false);
        }
    }, [connectionId]);

    useEffect(() => {
        load();
    }, [load]);

    const validationError = useMemo(() => {
        if (!form) return null;
        const numericKeys = Object.keys(form).filter(
            (key) => key !== "is_active" && key !== "direction_mode"
        ) as Array<keyof SpreadMoverForm>;
        for (const key of numericKeys) {
            const value = Number(form[key]);
            if (!form[key] || !Number.isFinite(value) || value < 0) {
                return "Все числовые параметры должны быть заполнены и не могут быть отрицательными";
            }
        }
        if (Number(form.check_time_min) < 1 || Number(form.check_time_max) < 1) {
            return "Интервал проверки должен быть не меньше 1 секунды";
        }
        for (const [minKey, maxKey, message] of RANGE_PAIRS) {
            if (Number(form[maxKey]) < Number(form[minKey])) return message;
        }
        return null;
    }, [form]);

    const updateDecimal = (key: EditableDecimalField, value: string) => {
        setForm((current) => current ? { ...current, [key]: value } : current);
    };

    const handleSave = async () => {
        if (!form || validationError) return;
        const payload: EditSpreadMoverPayload = {
            ...form,
            check_time_min: Number(form.check_time_min),
            check_time_max: Number(form.check_time_max),
        };
        setSaving(true);
        try {
            const saved = await saveSpreadMover(connectionId, payload);
            setServerData(saved);
            setForm(toForm(saved));
            showAlert({ type: "success", message: "Настройки Spread Mover сохранены" });
        } catch (error) {
            showAlert({ type: "error", message: getErrorMessage(error) });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <Box sx={{ display: "grid", placeItems: "center", minHeight: 280 }}><CircularProgress /></Box>;
    }

    if (loadError || !form) {
        return (
            <Alert severity="error" action={<Button color="inherit" onClick={load}>Повторить</Button>}>
                Не удалось загрузить настройки Spread Mover.
            </Alert>
        );
    }

    return (
        <Stack spacing={2.5}>
            <Box>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between">
                    <Box>
                        <Typography variant="h5" fontWeight={700}>Spread Mover</Typography>
                        <Typography color="text.secondary">
                            Автоматически двигает спред для {pairLabel} в заданных безопасных границах.
                        </Typography>
                    </Box>
                    <Chip
                        color={form.is_active ? "success" : "default"}
                        label={form.is_active ? "Будет включён" : "Выключен"}
                    />
                </Stack>
            </Box>

            <Alert severity="info">
                Изменения, включая переключатель активности, применяются после нажатия «Сохранить».
                Значения цены и объёма указываются в единицах торговой пары.
            </Alert>

            <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={2}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={form.is_active}
                                onChange={(event) => setForm({ ...form, is_active: event.target.checked })}
                            />
                        }
                        label={form.is_active ? "Автоматический сдвиг включён" : "Автоматический сдвиг выключен"}
                    />
                    <Divider />
                    <Typography variant="subtitle1" fontWeight={700}>Периодичность</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Система выбирает случайную паузу между минимумом и максимумом перед следующим сдвигом.
                    </Typography>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                        <TextField
                            type="number"
                            label="Минимальный интервал"
                            value={form.check_time_min}
                            onChange={(event) => setForm({ ...form, check_time_min: event.target.value })}
                            helperText="Секунды"
                            inputProps={{ min: 1, step: 1 }}
                            fullWidth
                        />
                        <TextField
                            type="number"
                            label="Максимальный интервал"
                            value={form.check_time_max}
                            onChange={(event) => setForm({ ...form, check_time_max: event.target.value })}
                            helperText="Секунды"
                            inputProps={{ min: 1, step: 1 }}
                            fullWidth
                        />
                    </Box>
                    <FormControl fullWidth>
                        <InputLabel id="spread-mover-direction-label">Направление движения</InputLabel>
                        <Select
                            labelId="spread-mover-direction-label"
                            label="Направление движения"
                            value={form.direction_mode}
                            onChange={(event) => setForm({ ...form, direction_mode: event.target.value as SpreadMoverDirection })}
                        >
                            <MenuItem value="RANDOM">Случайно — вверх или вниз на каждом шаге</MenuItem>
                            <MenuItem value="UP">Только вверх</MenuItem>
                            <MenuItem value="DOWN">Только вниз</MenuItem>
                        </Select>
                    </FormControl>
                </Stack>
            </Paper>

            {FIELD_GROUPS.map((group) => (
                <Paper key={group.title} variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle1" fontWeight={700}>{group.title}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{group.description}</Typography>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                        {group.fields.map((field) => (
                            <TextField
                                key={field.key}
                                type="number"
                                label={field.label}
                                value={form[field.key]}
                                onChange={(event) => updateDecimal(field.key, event.target.value)}
                                helperText={`${field.hint}${field.suffix ? `, ${field.suffix}` : ""}`}
                                inputProps={{ min: 0, step: "any" }}
                                fullWidth
                            />
                        ))}
                    </Box>
                </Paper>
            ))}

            {validationError && <Alert severity="warning">{validationError}</Alert>}

            <Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50" }}>
                <Typography variant="body2" color="text.secondary">
                    Последний успешный сдвиг: {formatDate(serverData?.last_move_at)}<br />
                    Следующий запланированный сдвиг: {formatDate(serverData?.next_move_at)}
                </Typography>
            </Paper>

            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                    variant="contained"
                    size="large"
                    startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveOutlinedIcon />}
                    disabled={saving || Boolean(validationError)}
                    onClick={handleSave}
                >
                    Сохранить
                </Button>
            </Box>
        </Stack>
    );
}
